import './type-tints.css';

/** How many soft shades there are; see type-tints.css. */
const SHADES = 8;

const hash = (value: string) => {
  let result = 2166136261;
  for (let index = 0; index < value.length; index++)
    result = Math.imul(result ^ value.charCodeAt(index), 16777619);
  return result >>> 0;
};

/**
 * One soft shade per document type, so a type looks the same everywhere: on the setup
 * screen while it is being learned, on its card in Document types and on its own screen
 * (owner, October 3, 2026: "can't we have consistent coloring?"). The shade only says
 * "this type"; it never means good or bad.
 *
 * It follows the type's NAME, because a type being learned has no id yet (the engine
 * gives a proposed type a fresh one), and it is worked out over the names in alphabetical
 * order, so the order the types arrive in or are listed in never changes a shade. Two
 * types share a shade only when a job has more types than there are shades. Renaming a
 * type gives it the name's shade. Returned by name.
 */
const shadeKey = (name: string) => name.trim().toLocaleLowerCase();
export function typeShades(types: readonly { name: string }[]): ReadonlyMap<string, number> {
  const keys = [...new Set(types.map((type) => shadeKey(type.name)))].sort();
  const byKey = new Map<string, number>();
  const used = new Set<number>();
  for (const key of keys) {
    let shade = hash(key) % SHADES;
    for (let tries = 0; used.has(shade) && tries < SHADES; tries++) shade = (shade + 1) % SHADES;
    byKey.set(key, shade);
    used.add(shade);
    if (used.size === SHADES) used.clear();
  }
  return new Map(types.map((type) => [type.name, byKey.get(shadeKey(type.name)) ?? 0]));
}
